import type { INewActivity } from "@/api/Activities";
import { ANY_TIME_OF_DAY } from "@/api/Activities";
import { type INewActivityWithOptionalInvoicingCodes } from "@/api/Invoicing";
import {
  activityFrequencySchema,
  recurringOptionSchema,
} from "@/forms/AddActivityForm/Scheduling/schedulingModels";
import { generateRandomUUID } from "@/Utils/UniqueId";
import {
  calendarDateSchema,
  dateInputSchema,
} from "@medoma/models/date-and-time";
import {
  getTimespan,
  getTimeFields,
} from "@/forms/AddActivityForm/activityTimeUtils";
import { categorySchema, timeOfDaySchema } from "@medoma/models/activities";
import { convertDurationToMinutes } from "@/forms/AddActivityForm/activityDurationUtils";
import { type BatchTemplateFormFields } from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";

export type IBatchTemplateActivity =
  BatchTemplateFormFields["activities"][number];

/**
 * Creates a new activity from a batch template activity.
 *
 * This function transforms a batch template activity into the appropriate INewActivity type
 * based on the activity's category.
 */
export const newActivityFromBatchTemplateActivity = ({
  activity,
  startDate,
  endDate,
  patientId,
  templateId,
  templateRevision,
}: {
  activity: IBatchTemplateActivity;
  startDate: string;
  endDate: string | undefined;
  patientId: string;
  templateId: string;
  templateRevision: number;
}): INewActivityWithOptionalInvoicingCodes => {
  const {
    title,
    category,
    description,
    doubleStaffing,
    frequency,
    recurrence,
    customRecurrenceInterval,
    customRecurrenceIntervalUnit,
    requiredCompetences,
    timeCategory,
    weekdays,
    hidden,
    measurements,
    timeslots,
    timeSensitivity,
    durationOption,
    customDuration,
    customDurationUnit,
    procedureCodes,
    productCodes,
  } = activity;

  const procedureCodesIds = procedureCodes ?? [];
  const productCodesIds = productCodes ?? [];

  // Calculate duration in minutes
  const duration = convertDurationToMinutes(
    durationOption ?? "15",
    customDuration ?? null,
    customDurationUnit ?? null,
  );

  const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
  const isRecurring =
    frequency === activityFrequencySchema.enum.recurring && recurrence !== null;
  const isRecurringAsInterval =
    recurrence !== recurringOptionSchema.enum.onSelectedWeekdays;

  const commonActivityFields: Pick<
    INewActivity,
    | "activityId"
    | "category"
    | "description"
    | "duration"
    | "hidden"
    | "startDate"
    | "timespan"
    | "title"
  > = {
    activityId: generateRandomUUID(),
    category,
    description: description?.trim(),
    duration,
    hidden: hidden === true || false,
    startDate: calendarDateSchema.decode(startDate),
    timespan: getTimespan({
      isAnyTimeOfDay,
      timeSensitivity: timeSensitivity ?? undefined,
    }),
    title: title.trim(),
  };

  const recurringTimeOfDayFields = isAnyTimeOfDay
    ? {
        isAnyTimeOfDay: true as const,
        timeslots: undefined,
      }
    : {
        isAnyTimeOfDay: false as const,
        timeslots: timeslots as {
          time: string;
        }[],
      };

  const timeFields = isRecurring
    ? {
        ...getTimeFields({
          isRecurringAsInterval,
          weekdays: weekdays ?? undefined,
          recurrence: recurrence!,
          customRecurrenceInterval: customRecurrenceInterval ?? null,
          customRecurrenceIntervalUnit: customRecurrenceIntervalUnit ?? null,
          ...recurringTimeOfDayFields,
        }),
        endDate: dateInputSchema.decode(endDate ?? "") ?? undefined,
      }
    : {
        time: isAnyTimeOfDay
          ? ANY_TIME_OF_DAY
          : (timeslots?.[0]?.time ?? ANY_TIME_OF_DAY),
      }; // TODO: This is BAD! Fix schemas: https://linear.app/medoma/issue/MED-7319/edit-schemas-to-create-safe-typing-for-timefields

  switch (category) {
    case categorySchema.enum.VideoCall: {
      return {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.VideoCall,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        type: "DigitalVisit",
        templateId,
        templateRevision,
      };
    }
    case categorySchema.enum.HomeVisit: {
      return {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.HomeVisit,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        doubleStaffing: doubleStaffing ?? false,
        procedureCodes: procedureCodesIds,
        productCodes: productCodesIds,
        templateId,
        templateRevision,
      };
    }
    case categorySchema.enum.ClinicVisit: {
      return {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.ClinicVisit,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        procedureCodes: procedureCodesIds,
        productCodes: productCodesIds,
        templateId,
        templateRevision,
      };
    }
    case categorySchema.enum.PatientTask: {
      return {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.PatientTask,
        ...timeFields,
        templateId,
        templateRevision,
      };
    }
    case categorySchema.enum.AdminTask: {
      return {
        ...commonActivityFields,
        patientId: patientId ? patientId : null,
        category: categorySchema.enum.AdminTask,
        requiredCompetences: requiredCompetences ? requiredCompetences : [],
        ...timeFields,
        procedureCodes: procedureCodesIds,
        productCodes: productCodesIds,
        templateId,
        templateRevision,
      };
    }
    case categorySchema.enum.PatientMeasurementTask: {
      return {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.PatientMeasurementTask,
        measurements: measurements ?? [],
        ...timeFields,
        templateId,
        templateRevision,
      };
    }
    case categorySchema.enum.PhoneCall: {
      return {
        ...commonActivityFields,
        patientId,
        category: categorySchema.enum.PhoneCall,
        requiredCompetences: requiredCompetences ?? [],
        ...timeFields,
        templateId,
        templateRevision,
      };
    }
    default:
      throw category satisfies never;
  }
};
